import React, { useMemo, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { Stage, Layer, Rect, Line, Circle, Text, Group, Arrow } from 'react-konva';
import { useSceneStore } from '../../store/sceneStore';
import { confirm } from '../../../../shared/utils/overlays';
import { useViewportOrientation } from '../../context/ViewportOrientationContext';

// Authoring stage uses a fixed virtual coordinate space (yards × pixel scale)
// and is then visually scaled to fit whatever width the parent column gives
// us. Without this, a 900px Stage pinned at its native size will paint past
// the column edge and visually cover the right-hand sidebar (where the
// coach's narration editor lives) — that's the "narration hidden behind the
// field" bug coaches kept reporting in the Designer view.
//
// Orientation aware:
//   - landscape: stage = LONG × SHORT (900 × 480), field +x is screen +x.
//   - portrait:  stage = SHORT × LONG (480 × 900), field +x is screen -y
//                (so offense at low x sits at the bottom moving up — the
//                 playbook view coaches asked for).
const NATIVE_LONG_AXIS = 900;
const NATIVE_SHORT_AXIS = 480;
const MIN_STAGE_W_LANDSCAPE = 360;
const MIN_STAGE_W_PORTRAIT = Math.round(MIN_STAGE_W_LANDSCAPE * NATIVE_SHORT_AXIS / NATIVE_LONG_AXIS);

function nativeStageDims(orientation) {
  if (orientation === 'portrait') {
    return { W: NATIVE_SHORT_AXIS, H: NATIVE_LONG_AXIS, minW: MIN_STAGE_W_PORTRAIT };
  }
  return { W: NATIVE_LONG_AXIS, H: NATIVE_SHORT_AXIS, minW: MIN_STAGE_W_LANDSCAPE };
}

// Coords: x = downfield yards (0–100), y = sideline yards (0–53.3, 26.65 mid).
// We keep the math in terms of NATIVE_*_AXIS so it doesn't change as the
// Stage gets visually scaled by ResizeObserver — the responsive layer is
// purely a CSS transform on the Stage container.
//
// `sx` and `sy` are *axis-pixel-density* scales: pxPerYard along whichever
// stage dimension the field's downfield / across axis maps to. The exact
// formula depends on orientation; consumers compute them once via
// `axisScales(orientation, coord)` and pass them to fieldToStage.
function axisScales(orientation, coord) {
  const { W, H } = nativeStageDims(orientation);
  if (orientation === 'portrait') {
    return {
      // x_field (0..100 yd downfield) → vertical axis of the stage.
      pxPerDown: H / coord.width,
      // y_field (0..53.3 yd across) → horizontal axis of the stage.
      pxPerAcross: W / coord.height,
    };
  }
  return {
    // x_field → horizontal stage axis (legacy)
    pxPerDown: W / coord.width,
    // y_field → vertical stage axis (inverted so y=0 is bottom)
    pxPerAcross: H / coord.height,
  };
}

function fieldToStage(pos, orientation, scales, dims) {
  if (orientation === 'portrait') {
    return {
      x: pos.y * scales.pxPerAcross,
      y: dims.H - pos.x * scales.pxPerDown,
    };
  }
  return {
    x: pos.x * scales.pxPerDown,
    y: dims.H - pos.y * scales.pxPerAcross,
  };
}

function stageToField(px, py, orientation, scales, dims) {
  if (orientation === 'portrait') {
    return {
      x: +(((dims.H - py) / scales.pxPerDown)).toFixed(2),
      y: +(px / scales.pxPerAcross).toFixed(2),
    };
  }
  return {
    x: +(px / scales.pxPerDown).toFixed(2),
    y: +(((dims.H - py) / scales.pxPerAcross)).toFixed(2),
  };
}

export default function SceneCanvas() {
  const scene = useSceneStore((s) => s.scene);
  const eventIdx = useSceneStore((s) => s.selectedEventIndex);
  const selectedActorId = useSceneStore((s) => s.selectedActorId);
  const selectActor = useSceneStore((s) => s.selectActor);
  const moveActor = useSceneStore((s) => s.moveActor);
  const coord = useSceneStore((s) => s.coordSystem);
  const actorRoles = useSceneStore((s) => s.actorRoles);
  const updateEvent = useSceneStore((s) => s.updateEvent);
  const setMeta = useSceneStore((s) => s.setMeta);

  // Orientation flag: 'portrait' under coach-app (playbook view, end zone
  // up, offense moving up the page) or 'landscape' under learner-app
  // (broadcast view). Drives stage dims, yard line layout, and the field-
  // to-stage transform. The active value is the resolved context value
  // — the SceneDesignerPage's "view as learner" preview toggle is handled
  // upstream by passing an override into RenderRouter, not by re-reading
  // here, because the coach designer's authoring canvas should ALWAYS
  // match the coach default (portrait) regardless of the preview toggle.
  const { orientation } = useViewportOrientation();
  const dims = useMemo(() => nativeStageDims(orientation), [orientation]);
  const scales = useMemo(() => axisScales(orientation, coord), [orientation, coord]);

  // Route-authoring tool state (lives only in this component — scene store
  // already has a heavy zundo-tracked tree, no need to put draft routes there)
  const [routeMode, setRouteMode] = useState(false);
  const [draftRoute, setDraftRoute] = useState(null);
  // draftRoute = { actorId, waypoints: [{x,y}, ...] }  (in field coords)

  // Sprint 2 — Telestration drawing tools (arrow/circle/freehand). When
  // a tool is active, all pointer events on the stage feed the in-flight
  // stroke instead of selecting actors. Strokes commit to scene-level
  // telestration on pointer-up, persisted in `scene.telestration`.
  // Why scene-level: most chalk-talk strokes are static install diagrams
  // visible across the whole play. Per-event chalk-talk lives in
  // event.telestration but the UI for it is a Sprint 5 follow-up.
  const [teleTool, setTeleTool] = useState(null); // 'arrow' | 'circle' | 'freehand' | null
  // Default ink: dark slate. Reads on both the green football field and
  // the white whiteboard. Coaches can change it via the colour swatch.
  const [teleColor, setTeleColor] = useState('#1a1a1a');
  const [draftStroke, setDraftStroke] = useState(null);

  // Responsive Stage: measure the wrapper, then scale Stage to fit.
  // Portrait mode is TALLER than wide (480×900 native), so we must
  // constrain on BOTH dimensions — width-only scaling lets the stage
  // overflow the viewport vertically, requiring the coach to scroll to
  // see the full field and clipping the bottom players.
  //
  // Strategy: compute the scale that fits the stage inside the available
  // bounding box (min of width-fit and height-fit), then apply it.
  // Never scale UP past native size (avoids pixelation on large monitors)
  // and never shrink below the orientation minimum (keeps labels legible).
  const wrapperRef = useRef(null);
  // stageAreaRef measures only the area below the toolbar (the region the
  // stage frame can actually fill). Using wrapperRef for height included the
  // toolbar height in availH, causing the computed stage height to overflow
  // the wrapper by ~(toolbarHeight) pixels and clip the bottom of the field.
  const stageAreaRef = useRef(null);
  const [stageScale, setStageScale] = useState(1);

  // Shared measurement helper — always reads stageAreaRef.current
  // dynamically so it works even if stageAreaRef mounts after the
  // initial effect run (which happens when the scene loads asynchronously).
  const computeScale = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const availW = el.clientWidth;
    if (availW <= 0) return;
    // Use the stage-area height (excludes toolbar) so the calculated
    // stageScale actually fits within the remaining space.
    const stageEl = stageAreaRef.current;
    const toolbarEl = el.querySelector('.scene-canvas-toolbar');
    const toolbarH = toolbarEl ? toolbarEl.offsetHeight : 0;
    const availH = stageEl ? stageEl.clientHeight : Math.max(0, el.clientHeight - toolbarH - 8);
    const scaleW = Math.min(dims.W, availW) / dims.W;
    const scaleH = availH > 0 ? Math.min(dims.H, availH) / dims.H : 1;
    const scale = Math.min(scaleW, scaleH);
    const targetW = Math.max(dims.minW, Math.round(scale * dims.W));
    setStageScale(targetW / dims.W);
  }, [dims.W, dims.H, dims.minW]);

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    computeScale();
    const ro = new ResizeObserver(computeScale);
    ro.observe(wrapperRef.current);
    if (stageAreaRef.current) ro.observe(stageAreaRef.current);
    return () => ro.disconnect();
  }, [dims.W, dims.H, dims.minW, computeScale]);

  // Re-measure when the scene loads: until the scene is non-null the
  // stageAreaRef div isn't in the DOM (the "No scene loaded" branch renders
  // instead), so the initial effect captures stageAreaRef.current = null and
  // falls back to the full wrapper height. This second effect fires whenever
  // the scene changes and recalculates with the now-available stageAreaRef.
  useLayoutEffect(() => {
    if (!scene) return;
    computeScale();
  }, [scene, computeScale]);

  // Backwards-compat aliases for inline coordinate math below — these
  // point to whichever stage axis the field's downfield / across axis
  // currently maps to, derived from `scales` + `dims`.
  //   sx = px-per-yard along the stage's HORIZONTAL axis
  //   sy = px-per-yard along the stage's VERTICAL axis (always inverted
  //        for fieldToStage so y_field=0 is at the bottom of the stage).
  const sx = orientation === 'portrait' ? scales.pxPerAcross : scales.pxPerDown;
  const sy = orientation === 'portrait' ? scales.pxPerDown : scales.pxPerAcross;

  // Closure-bound transforms — every call site below uses these so the
  // orientation flip never leaks past this component.
  const f2s = useCallback((pos) => fieldToStage(pos, orientation, scales, dims), [orientation, scales, dims]);
  const s2f = useCallback((px, py) => stageToField(px, py, orientation, scales, dims), [orientation, scales, dims]);

  const event = scene?.timeline?.[eventIdx];

  const roleColor = useMemo(() => {
    const m = {};
    actorRoles.forEach((r) => (m[r.role_id] = r.color));
    return m;
  }, [actorRoles]);

  const startRouteForSelectedActor = useCallback(() => {
    if (!selectedActorId) {
      alert('Click a player on the field first, then start the Route tool.');
      return;
    }
    setRouteMode(true);
    setDraftRoute({ actorId: selectedActorId, waypoints: [] });
  }, [selectedActorId]);

  const cancelRoute = useCallback(() => {
    setRouteMode(false);
    setDraftRoute(null);
  }, []);

  const finishRoute = useCallback(() => {
    if (!draftRoute || draftRoute.waypoints.length === 0) {
      cancelRoute();
      return;
    }
    const annotation = {
      type: 'route',
      actors: [draftRoute.actorId],
      waypoints: draftRoute.waypoints.map((w) => ({ x: w.x, y: w.y })),
      label: 'Route',
    };
    const existing = event?.annotations || [];
    updateEvent(eventIdx, { annotations: [...existing, annotation] });
    cancelRoute();
  }, [draftRoute, event, eventIdx, updateEvent, cancelRoute]);

  // ── Spotlight authoring ──────────────────────────────────────────────────
  // Coaches asked for a way to pick which player(s) get highlighted on a
  // given event ("for the 1-3-5-7 technique lesson, ring the right defender
  // each step"). Rather than building a separate annotation modal, we let
  // the coach select a player on the field and toggle a per-event spotlight
  // with this single button. The annotation matches the runtime contract
  // already used by DFQ scenes: AnnotationRenderer3D dims non-target actors
  // and rings the highlighted ones, AnnotationRenderer (2D) below mirrors it.
  const spotlightedActors = useMemo(() => {
    return (event?.annotations || [])
      .filter((a) => a.type === 'spotlight')
      .flatMap((a) => a.actors || []);
  }, [event]);
  const isSelectedActorSpotlit = !!selectedActorId && spotlightedActors.includes(selectedActorId);

  const toggleSpotlightForSelected = useCallback(() => {
    if (!selectedActorId) {
      alert('Click a player on the field first, then toggle Spotlight.');
      return;
    }
    const existing = event?.annotations || [];
    const others = existing.filter((a) => a.type !== 'spotlight');
    const currentSpot = existing.find((a) => a.type === 'spotlight') || null;
    const currentActors = currentSpot?.actors || [];
    let nextActors;
    if (currentActors.includes(selectedActorId)) {
      nextActors = currentActors.filter((id) => id !== selectedActorId);
    } else {
      nextActors = [...currentActors, selectedActorId];
    }
    const nextAnnotations = [...others];
    if (nextActors.length > 0) {
      nextAnnotations.push({
        type: 'spotlight',
        actors: nextActors,
        label: currentSpot?.label || 'Spotlight',
        // Same dim level the DFQ seed uses, so 2D and 3D feel identical.
        dim_opacity: currentSpot?.dim_opacity ?? 0.25,
        style: currentSpot?.style || { color: '0xffd700' },
      });
    }
    updateEvent(eventIdx, { annotations: nextAnnotations });
  }, [selectedActorId, event, eventIdx, updateEvent]);

  // Konva's stage.getPointerPosition() returns the cursor position
  // RELATIVE TO THE STAGE'S CONTAINER in screen pixels — i.e. it does
  // NOT divide out the Stage's scaleX/scaleY transform we applied to
  // make the canvas responsive (see `stageScale`). On any monitor
  // narrow enough that `stageScale < 1` (every laptop, every modal),
  // a click reported as `(200, 100)` actually corresponds to a stage-
  // local point of `(200/scale, 100/scale)`. Without this divide,
  // strokes/route waypoints landed at a point further up-and-left
  // than the cursor, by a factor of (1 - stageScale). That is the
  // "circle appears at a different location than where I clicked"
  // bug the coach reported on the whiteboard.
  const pointerToStage = useCallback((stage) => {
    const p = stage.getPointerPosition();
    if (!p) return null;
    const s = stageScale || 1;
    return { x: p.x / s, y: p.y / s };
  }, [stageScale]);

  const handleStageClick = useCallback((e) => {
    if (!routeMode || !draftRoute) return;
    const stage = e.target.getStage();
    const pointer = pointerToStage(stage);
    if (!pointer) return;
    const fp = s2f(pointer.x, pointer.y);
    setDraftRoute((d) => ({ ...d, waypoints: [...d.waypoints, fp] }));
  }, [routeMode, draftRoute, s2f, pointerToStage]);

  // ── Telestration pointer handlers ────────────────────────────────────────
  const startStroke = useCallback((e) => {
    if (!teleTool) return;
    const stage = e.target.getStage();
    const pointer = pointerToStage(stage);
    if (!pointer) return;
    const fp = s2f(pointer.x, pointer.y);
    setDraftStroke({ tool: teleTool, points: [fp] });
  }, [teleTool, s2f, pointerToStage]);

  const extendStroke = useCallback((e) => {
    if (!teleTool || !draftStroke) return;
    const stage = e.target.getStage();
    const pointer = pointerToStage(stage);
    if (!pointer) return;
    const fp = s2f(pointer.x, pointer.y);
    if (teleTool === 'freehand') {
      setDraftStroke((d) => (d ? { ...d, points: [...d.points, fp] } : d));
    } else {
      // arrow + circle: track only the second (anchor) point; replace it.
      setDraftStroke((d) => (d ? { ...d, points: [d.points[0], fp] } : d));
    }
  }, [teleTool, draftStroke, s2f, pointerToStage]);

  const finishStroke = useCallback(() => {
    if (!draftStroke || draftStroke.points.length < 1) {
      setDraftStroke(null);
      return;
    }
    const tool = draftStroke.tool;
    const stroke = {
      stroke_id: `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      type: tool,
      points: draftStroke.points.map((p) => ({ x: p.x, y: p.y })),
      color: teleColor,
      stroke_width: 0.4,
      dashed: false,
      label: null,
      metadata: {},
    };
    // Strokes that are too short / single-point in non-freehand tools
    // are discarded — usually a stray click while panning.
    if (tool !== 'freehand' && stroke.points.length < 2) {
      setDraftStroke(null);
      return;
    }
    const existing = scene?.telestration || [];
    setMeta({ telestration: [...existing, stroke] });
    setDraftStroke(null);
  }, [draftStroke, teleColor, scene, setMeta]);

  const removeLastStroke = useCallback(() => {
    const existing = scene?.telestration || [];
    if (existing.length === 0) return;
    setMeta({ telestration: existing.slice(0, -1) });
  }, [scene, setMeta]);

  // ── Stamp / pre-made shape palette ──────────────────────────────────────
  // Coaches asked for one-click drop of common chalk-talk symbols (O for
  // offense, X for defense, arrows). Free-drawing every shape every time
  // is slow when you're sketching a 20-player install. Stamps drop a
  // pre-shaped stroke at the centre of the canvas; the coach then drags
  // it into position. Persisted into the same `scene.telestration`
  // bucket as drawn strokes, so save/load + preview render share one
  // code path.
  const dropStamp = useCallback((kind) => {
    // Centre of the field in scene coordinates (same coord system used
    // everywhere else in this component).
    const cx = +(coord.width / 2).toFixed(2);
    const cy = +(coord.height / 2).toFixed(2);
    const id = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    let stroke;
    if (kind === 'o') {
      stroke = {
        stroke_id: id,
        type: 'circle',
        // [centre, edge] — radius ≈ 2 yards so it lands as a clearly
        // visible donut even on shrunken displays.
        points: [{ x: cx, y: cy }, { x: cx + 2.0, y: cy }],
        color: teleColor,
        stroke_width: 0.45,
        dashed: false,
        label: 'O',
        metadata: { stamp: 'o' },
      };
    } else if (kind === 'x') {
      stroke = {
        stroke_id: id,
        type: 'text',
        points: [{ x: cx - 0.7, y: cy + 0.7 }],
        color: teleColor,
        stroke_width: 0.45,
        dashed: false,
        text: 'X',
        label: 'X',
        metadata: { stamp: 'x' },
      };
    } else if (kind === 'arrow') {
      stroke = {
        stroke_id: id,
        type: 'arrow',
        points: [{ x: cx - 5, y: cy }, { x: cx + 5, y: cy }],
        color: teleColor,
        stroke_width: 0.45,
        dashed: false,
        label: null,
        metadata: { stamp: 'arrow' },
      };
    } else {
      return;
    }
    const existing = scene?.telestration || [];
    setMeta({ telestration: [...existing, stroke] });
  }, [coord, teleColor, scene, setMeta]);

  // Whiteboard scenes: allow direct drag of any committed stroke so the
  // coach can lay shapes down with a stamp and then nudge them into
  // position. We update the stroke's points in scene.telestration.
  //
  // Vector (delta) transforms differ by orientation:
  //   landscape: stage.x = field.x * pxPerDown,  stage.y = dims.H - field.y * pxPerAcross
  //              → field_dx = stage_dx / pxPerDown, field_dy = -stage_dy / pxPerAcross
  //   portrait:  stage.x = field.y * pxPerAcross, stage.y = dims.H - field.x * pxPerDown
  //              → field_dx = -stage_dy / pxPerDown, field_dy = stage_dx / pxPerAcross
  // (Same axis swap as `fieldToStage`, just on the linear part of the
  // affine map so a drag delta translates correctly.)
  const isWhiteboard = scene?.scene_type === 'whiteboard';
  const onStrokeDragMove = useCallback((stroke, evt) => {
    const dxPx = evt.target.x();
    const dyPx = evt.target.y();
    let dxField, dyField;
    if (orientation === 'portrait') {
      dxField = -dyPx / scales.pxPerDown;
      dyField = dxPx / scales.pxPerAcross;
    } else {
      dxField = dxPx / scales.pxPerDown;
      dyField = -dyPx / scales.pxPerAcross;
    }
    const next = (scene?.telestration || []).map((s) => {
      if (s.stroke_id !== stroke.stroke_id) return s;
      return {
        ...s,
        points: s.points.map((p) => ({
          x: +(p.x + dxField).toFixed(2),
          y: +(p.y + dyField).toFixed(2),
        })),
      };
    });
    setMeta({ telestration: next });
    evt.target.position({ x: 0, y: 0 });
  }, [scene, setMeta, orientation, scales]);

  const clearAllStrokes = useCallback(() => {
    if (!(scene?.telestration?.length)) return;
    confirm({
      title: 'Clear telestration?',
      message: 'Clear all telestration on this scene?',
      confirmLabel: 'Clear',
      danger: true,
    }).then((ok) => {
      if (!ok) return;
      setMeta({ telestration: [] });
    });
  }, [scene, setMeta]);

  // Esc cancels, Enter finishes
  React.useEffect(() => {
    if (!routeMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') cancelRoute();
      else if (e.key === 'Enter') finishRoute();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [routeMode, cancelRoute, finishRoute]);

  if (!scene) {
    return (
      <div ref={wrapperRef} className="scene-canvas-wrapper" style={{ background: '#0d3d2a',
                    minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
        No scene loaded
      </div>
    );
  }

  const yardLines = [];
  for (let yd = 10; yd < coord.width; yd += 10) {
    yardLines.push(yd);
  }

  // Helper: actor position at the current event (falls back to initial_state)
  const actorPos = (actorId) => {
    const a = scene.actors?.find((x) => x.actor_id === actorId);
    if (!a) return { x: 50, y: 26.65 };
    return event?.actor_states?.[actorId]?.position || a.initial_state?.position || { x: 50, y: 26.65 };
  };

  // Outer wrapper holds the toolbar + a fixed-aspect Stage container that
  // we ResizeObserve. The Stage itself is rendered at its native dimensions
  // and CSS-scaled to fit — react-konva canvas scales cleanly without
  // re-laying-out child coordinates.
  return (
    <div ref={wrapperRef} className="scene-canvas-wrapper">
      <div className="scene-canvas-toolbar">
        {!routeMode && !teleTool ? (
          <>
            {/* Player-centric tools (Route, Spotlight) only matter for spatial
                scenes that have actors — whiteboard scenes are pure chalk-talk
                with no players, so we hide them to reduce confusion. */}
            {scene?.scene_type !== 'whiteboard' && (
              <>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={startRouteForSelectedActor}
                  title="Select a player, then click on the field to add waypoints. Press Enter to finish, Esc to cancel."
                >➤ Route tool</button>
                <button
                  type="button"
                  className={isSelectedActorSpotlit ? 'btn-warning btn-sm' : 'btn-secondary btn-sm'}
                  onClick={toggleSpotlightForSelected}
                  title="Highlight the selected player on this event. Other players will be dimmed during playback. Click again to remove."
                  data-testid="spotlight-toggle"
                  disabled={!selectedActorId}
                >
                  {isSelectedActorSpotlit ? '★ Remove spotlight' : '★ Spotlight selected'}
                </button>
                <span className="scene-canvas-toolbar-divider" aria-hidden>|</span>
              </>
            )}
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setTeleTool('arrow')}
              title="Draw an arrow on the field. Click-and-drag to set start/end."
            >↗ Arrow</button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setTeleTool('circle')}
              title="Draw a circle on the field. Click-and-drag to set centre/radius."
            >○ Circle</button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setTeleTool('freehand')}
              title="Freehand draw. Press-and-drag to sketch."
            >✎ Freehand</button>
            {/* Stamps — only on whiteboard scenes (coaches asked for one-
                click drop of common chalk-talk symbols, which can then
                be dragged into position rather than free-drawn). */}
            {isWhiteboard && (
              <>
                <span className="scene-canvas-toolbar-divider" aria-hidden>|</span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => dropStamp('o')}
                  title="Drop an O (offense) symbol — drag to position."
                  data-testid="stamp-o"
                >Drop O</button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => dropStamp('x')}
                  title="Drop an X (defense) symbol — drag to position."
                  data-testid="stamp-x"
                >Drop X</button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => dropStamp('arrow')}
                  title="Drop an arrow — drag the body to position."
                  data-testid="stamp-arrow"
                >Drop ➤</button>
              </>
            )}
            <input
              type="color"
              value={teleColor}
              onChange={(e) => setTeleColor(e.target.value)}
              title="Stroke colour"
              style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
            />
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={removeLastStroke}
              disabled={!(scene?.telestration?.length)}
              title="Undo the last telestration stroke"
            >↶ Undo stroke</button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={clearAllStrokes}
              disabled={!(scene?.telestration?.length)}
              title="Clear every telestration stroke on this scene"
            >🗑 Clear all</button>
            <span className="scene-canvas-hint">
              {scene?.scene_type === 'whiteboard'
                ? 'Tip: drop O / X / arrow stamps and drag them into position, or pick Arrow / Circle / Freehand to draw. Hit Preview to see the diagram exactly as a learner will.'
                : 'Tip: drag any player to reposition. Click a player to select. Use Route to draw routes; Spotlight to highlight defenders; Arrow/Circle/Freehand for chalk-talk.'}
            </span>
          </>
        ) : routeMode ? (
          <>
            <span className="scene-canvas-route-status">
              ✏️ Drawing route for <strong>{scene.actors?.find((a) => a.actor_id === draftRoute?.actorId)?.label || draftRoute?.actorId}</strong>
              — click the field to add waypoints ({draftRoute?.waypoints.length || 0} so far)
            </span>
            <button className="btn-primary btn-sm" onClick={finishRoute} disabled={!draftRoute?.waypoints.length}>✓ Finish (Enter)</button>
            <button className="btn-ghost btn-sm" onClick={cancelRoute}>Cancel (Esc)</button>
          </>
        ) : (
          <>
            <span className="scene-canvas-route-status">
              ✏️ {teleTool === 'arrow' ? 'Arrow' : teleTool === 'circle' ? 'Circle' : 'Freehand'} —
              press-and-drag on the field. Release to commit the stroke.
            </span>
            <button className="btn-ghost btn-sm" onClick={() => { setTeleTool(null); setDraftStroke(null); }}>
              Done
            </button>
          </>
        )}
      </div>
      {/* Stage area — flex: 1 so it fills the space left after the toolbar.
          stageAreaRef measures only this area for accurate stageScale calc. */}
      <div
        ref={stageAreaRef}
        style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      >
      <div
        className="scene-canvas-stage-frame"
        style={{
          width: dims.W * stageScale,
          height: dims.H * stageScale,
          maxWidth: '100%',
          // Whiteboard = a literal whiteboard: white surface with a faint
          // grey grid. Earlier "dark surface" produced a black grid that
          // coaches kept describing as a "broken screen". Match the stage
          // colour so there is no seam at the edge.
          background: scene?.scene_type === 'whiteboard' ? '#fafafa' : '#0d3d2a',
          borderRadius: 6,
          overflow: 'hidden',
          // Whiteboard scenes get a subtle border so the coach can tell
          // where the editable canvas ends.
          border: scene?.scene_type === 'whiteboard' ? '2px solid #c8d0d8' : 'none',
          position: 'relative',
        }}
        data-orientation={orientation}
      >
      <Stage
        width={dims.W}
        height={dims.H}
        scaleX={stageScale}
        scaleY={stageScale}
        style={{
          // Sprint 4 — Whiteboard scene_type drops the football field
          // for a neutral chalk-talk surface. Coaches authoring concept
          // diagrams (read progressions, recovery flow) get a blank
          // white canvas; everything else (actor drag, telestration,
          // routes) still works.
          background: scene?.scene_type === 'whiteboard' ? '#fafafa' : '#0d3d2a',
          cursor: (routeMode || teleTool) ? 'crosshair' : 'default',
          display: 'block',
        }}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onMouseDown={teleTool ? startStroke : undefined}
        onMouseMove={(teleTool && draftStroke) ? extendStroke : undefined}
        onMouseUp={teleTool ? finishStroke : undefined}
        onTouchStart={teleTool ? startStroke : undefined}
        onTouchMove={(teleTool && draftStroke) ? extendStroke : undefined}
        onTouchEnd={teleTool ? finishStroke : undefined}
      >
        {/* Football field background — suppressed for whiteboard scenes. */}
        {scene?.scene_type !== 'whiteboard' && (
          <Layer listening={false}>
            <Rect x={0} y={0} width={dims.W} height={dims.H} fill="#1f6b3a" />
            {yardLines.map((yd) => {
              // Yard line endpoints in FIELD coords — sideline-to-sideline
              // across the field at downfield position `yd`. f2s flips
              // them to the stage's current orientation.
              const a = f2s({ x: yd, y: 0 });
              const b = f2s({ x: yd, y: coord.height });
              return (
                <Line
                  key={`yl-${yd}`}
                  points={[a.x, a.y, b.x, b.y]}
                  stroke={yd === 50 ? '#ffffff' : '#b6e0c0'}
                  strokeWidth={yd % 50 === 0 ? 2 : 1}
                  opacity={yd % 50 === 0 ? 0.9 : 0.35}
                />
              );
            })}
            {/* Yard NUMBERS rendered TWICE — once near each sideline,
                broadcast-graphic style. Centre-of-field placement
                hides the labels behind player markers. In portrait
                mode the yard lines are horizontal, so labels sit near
                the LEFT and RIGHT sidelines instead. */}
            {[10, 20, 30, 40, 50, 60, 70, 80, 90].flatMap((yd) => {
              const text = String(yd === 50 ? 50 : yd <= 50 ? yd : 100 - yd);
              if (orientation === 'portrait') {
                const left = f2s({ x: yd, y: 2 });
                const right = f2s({ x: yd, y: coord.height - 2 });
                return [
                  <Text
                    key={`lbl-left-${yd}`}
                    x={left.x - 14}
                    y={left.y - 8}
                    text={text}
                    fill="#ffffff"
                    fontSize={14}
                    fontStyle="bold"
                    opacity={0.85}
                  />,
                  <Text
                    key={`lbl-right-${yd}`}
                    x={right.x + 4}
                    y={right.y - 8}
                    text={text}
                    fill="#ffffff"
                    fontSize={14}
                    fontStyle="bold"
                    opacity={0.85}
                  />,
                ];
              }
              const top = f2s({ x: yd, y: coord.height - 2 });
              const bot = f2s({ x: yd, y: 2 });
              return [
                <Text
                  key={`lbl-top-${yd}`}
                  x={top.x - 10}
                  y={top.y + 6}
                  text={text}
                  fill="#ffffff"
                  fontSize={14}
                  fontStyle="bold"
                  opacity={0.85}
                />,
                <Text
                  key={`lbl-bot-${yd}`}
                  x={bot.x - 10}
                  y={bot.y - 18}
                  text={text}
                  fill="#ffffff"
                  fontSize={14}
                  fontStyle="bold"
                  opacity={0.85}
                />,
              ];
            })}
            {/* End zones — drawn in field coords so the rect orientation
                follows the active stage layout automatically. */}
            {(() => {
              const corners = [
                { x: 0, y: 0 }, { x: 10, y: 0 }, { x: 10, y: coord.height }, { x: 0, y: coord.height },
                { x: coord.width - 10, y: 0 }, { x: coord.width, y: 0 },
                { x: coord.width, y: coord.height }, { x: coord.width - 10, y: coord.height },
              ].map(f2s);
              const ezA = corners.slice(0, 4);
              const ezB = corners.slice(4, 8);
              const bbox = (pts) => {
                const xs = pts.map((p) => p.x);
                const ys = pts.map((p) => p.y);
                return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
              };
              const a = bbox(ezA);
              const b = bbox(ezB);
              return (
                <>
                  <Rect x={a.x} y={a.y} width={a.w} height={a.h} fill="#06532e" opacity={0.55} />
                  <Rect x={b.x} y={b.y} width={b.w} height={b.h} fill="#06532e" opacity={0.55} />
                </>
              );
            })()}
          </Layer>
        )}
        {/* Whiteboard mode: visible dot grid + label so the coach can tell
            this is the editable chalk-talk surface (early users reported it
            looked like a "broken black screen" because the previous 1px #444
            dots were invisible at scale). */}
        {scene?.scene_type === 'whiteboard' && (
          <Layer listening={false}>
            {/* Faint grey grid against #fafafa — gives the coach a sense
                of scale without overwhelming the strokes. Grid is
                authored in FIELD coords so the dots stay aligned to the
                same yard positions in either orientation. */}
            {Array.from({ length: 21 }, (_, i) => i * 5).map((xy) => (
              <React.Fragment key={`grid-${xy}`}>
                {Array.from({ length: 12 }, (_, j) => j * 5).map((yj) => {
                  const p = f2s({ x: xy, y: yj });
                  return (
                    <Circle
                      key={`dot-${xy}-${yj}`}
                      x={p.x}
                      y={p.y}
                      radius={1.2}
                      fill="#c8cdd3"
                      opacity={0.85}
                    />
                  );
                })}
              </React.Fragment>
            ))}
            {/* Empty-state hint: only when the coach hasn't drawn anything
                yet and isn't actively in a drawing tool. */}
            {!(scene?.telestration?.length) && !teleTool && (
              <>
                <Text
                  x={dims.W / 2 - 200}
                  y={dims.H / 2 - 44}
                  width={400}
                  align="center"
                  text="Whiteboard"
                  fill="#3b4350"
                  fontSize={28}
                  fontStyle="bold"
                  opacity={0.9}
                />
                <Text
                  x={dims.W / 2 - 280}
                  y={dims.H / 2 - 4}
                  width={560}
                  align="center"
                  text="Drop O / X / arrow stamps from the toolbar — drag to position. Or pick Arrow / Circle / Freehand and draw freehand."
                  fill="#5b6776"
                  fontSize={14}
                  opacity={0.9}
                />
                <Text
                  x={dims.W / 2 - 280}
                  y={dims.H / 2 + 22}
                  width={560}
                  align="center"
                  text="Hit Preview to see your diagram exactly as a learner will."
                  fill="#7a8392"
                  fontSize={12}
                  opacity={0.85}
                />
              </>
            )}
          </Layer>
        )}

        {/* Annotations layer (routes, zones, highlights) */}
        <Layer listening={false}>
          {(event?.annotations || []).map((ann, i) => (
            <AnnotationRenderer
              key={`ann-${i}`}
              annotation={ann}
              actorPos={actorPos}
              f2s={f2s}
              sx={sx}
              sy={sy}
            />
          ))}

          {/* In-progress draft route */}
          {routeMode && draftRoute && draftRoute.waypoints.length > 0 && (
            <RouteWaypoints
              startField={actorPos(draftRoute.actorId)}
              waypoints={draftRoute.waypoints}
              f2s={f2s}
              draft
            />
          )}
        </Layer>

        {/* Sprint 2 — Telestration layer (chalk-talk strokes).
            Whiteboard scenes get a listening layer + per-stroke
            draggable Groups so coaches can move stamps and drawn
            shapes after the fact (matches GoArmy's chalk-talk UX). */}
        <Layer listening={isWhiteboard && !teleTool}>
          {(scene?.telestration || []).map((stroke) => (
            isWhiteboard ? (
              <Group
                key={stroke.stroke_id}
                draggable={!teleTool}
                onDragEnd={(evt) => onStrokeDragMove(stroke, evt)}
              >
                <TelestrationStrokeRenderer
                  stroke={stroke}
                  f2s={f2s}
                  sx={sx}
                  sy={sy}
                  defaultColor="#1a1a1a"
                />
              </Group>
            ) : (
              <TelestrationStrokeRenderer
                key={stroke.stroke_id}
                stroke={stroke}
                f2s={f2s}
                sx={sx}
                sy={sy}
              />
            )
          ))}
          {draftStroke && draftStroke.points.length >= 1 && (
            <TelestrationStrokeRenderer
              stroke={{
                stroke_id: '__draft__',
                type: draftStroke.tool,
                points: draftStroke.points,
                color: teleColor,
                stroke_width: 0.4,
                dashed: true,
              }}
              f2s={f2s}
              sx={sx}
              sy={sy}
            />
          )}
        </Layer>

        <Layer>
          {(() => {
            // If any spotlight annotation is on this event, dim every actor
            // not in the highlighted set so the coach gets a true preview of
            // what the runtime will draw. dim_opacity is taken from the
            // first spotlight annotation; default 0.25 matches the DFQ seed.
            const spotAnns = (event?.annotations || []).filter((a) => a.type === 'spotlight');
            const hasSpot = spotAnns.length > 0;
            const spotIds = new Set(spotAnns.flatMap((a) => a.actors || []));
            const dimAlpha = spotAnns[0]?.dim_opacity ?? 0.25;

            // ── Pre-authored coverage routes ───────────────────────────────
            // Coaches author routes on actors via actor.initial_state.metadata.route.
            // The canonical color convention (matching coaching diagrams):
            //   • D-line (DE / DT / NT / DL) → blue  — forward pass rush
            //   • All other defense           → red   — coverage/zone drops
            //   • Offense                     → white — motion routes
            //   • actor.initial_state.metadata.route_color overrides all
            const DL_ROLES = new Set(['DE', 'DT', 'NT', 'DL']);
            const routeElements = (scene.actors || []).flatMap((actor) => {
              const route = actor.initial_state?.metadata?.route;
              if (!route || route.length === 0) return [];
              const startPos = actor.initial_state?.position;
              if (!startPos) return [];
              const isOffense = actor.team === 'offense';
              const routeColor = actor.initial_state.metadata.route_color
                || (isOffense
                    ? '#ffffff'
                    : DL_ROLES.has(actor.role) ? '#1565c0' : '#c62828');
              return [(
                <RouteWaypoints
                  key={`route-${actor.actor_id}`}
                  startField={startPos}
                  waypoints={route}
                  f2s={f2s}
                  color={routeColor}
                />
              )];
            });

            const actorElements = (scene.actors || []).map((actor) => {
              const pos = event?.actor_states?.[actor.actor_id]?.position || actor.initial_state?.position || { x: 50, y: 26.65 };
              const { x: px, y: py } = f2s(pos);
              const isOffense = actor.team === 'offense';
              // actor.appearance.color has priority so coaches can set custom colors
              const fill = roleColor[actor.role]
                || actor.appearance?.color
                || (isOffense ? '#ffffff' : actor.team === 'ball' ? '#8B4513' : '#e53935');
              const isSelected = actor.actor_id === selectedActorId;
              const dim = hasSpot && !spotIds.has(actor.actor_id);
              const opacity = dim ? Math.max(0, Math.min(1, dimAlpha)) : 1;

              return (
                <Group
                  key={actor.actor_id}
                  x={px}
                  y={py}
                  opacity={opacity}
                  draggable={!routeMode && !teleTool}
                  onDragMove={(e) => {
                    const newPx = Math.max(0, Math.min(dims.W, e.target.x()));
                    const newPy = Math.max(0, Math.min(dims.H, e.target.y()));
                    let fp = s2f(newPx, newPy);
                    // Sprint 4 — grid_snap rounds drag positions to the
                    // nearest N yards. Coaches use this to get clean
                    // alignments (e.g. snap every receiver to the same
                    // depth off the ball).
                    const snap = scene?.grid_snap;
                    if (snap && snap > 0) {
                      fp = {
                        x: +(Math.round(fp.x / snap) * snap).toFixed(2),
                        y: +(Math.round(fp.y / snap) * snap).toFixed(2),
                      };
                      // Reflect snap visually — otherwise the player
                      // glides smoothly while the underlying state jumps.
                      const snapped = f2s(fp);
                      e.target.position({ x: snapped.x, y: snapped.y });
                    }
                    moveActor(actor.actor_id, fp.x, fp.y);
                  }}
                  onClick={(e) => {
                    e.cancelBubble = true;
                    selectActor(actor.actor_id);
                  }}
                  onTap={(e) => {
                    e.cancelBubble = true;
                    selectActor(actor.actor_id);
                  }}
                >
                  {isSelected && <Circle radius={20} stroke="#fbbf24" strokeWidth={3} dash={[4, 4]} />}
                  {/* Offense = white circle / dark text (matches coaching diagram convention).
                      Defense = colored circle / white text. */}
                  <Circle
                    radius={14}
                    fill={fill}
                    stroke={isOffense ? '#333333' : '#ffffff'}
                    strokeWidth={isOffense ? 2 : 1.5}
                    shadowColor="black"
                    shadowBlur={isOffense ? 2 : 4}
                    shadowOpacity={0.35}
                  />
                  <Text
                    text={actor.label || actor.role || '?'}
                    fontSize={11}
                    fontStyle="bold"
                    fill={isOffense ? '#111111' : '#ffffff'}
                    width={40}
                    align="center"
                    offsetX={20}
                    offsetY={5}
                  />
                </Group>
              );
            });

            return [...routeElements, ...actorElements];
          })()}
        </Layer>
      </Stage>
      </div>
      </div>
    </div>
  );
}

// ─── Annotation rendering ────────────────────────────────────────────────────

function AnnotationRenderer({ annotation, actorPos, f2s, sx, sy }) {
  const a = annotation;
  if (a.type === 'route') {
    const actorId = (a.actors || [])[0];
    if (!actorId) return null;
    const start = actorPos(actorId);
    const wps = a.waypoints && a.waypoints.length
      ? a.waypoints
      : (a.position ? [a.position] : []);
    if (!wps.length) return null;
    return <RouteWaypoints startField={start} waypoints={wps} f2s={f2s} label={a.label} />;
  }
  if (a.type === 'highlight') {
    const actorId = (a.actors || [])[0];
    if (!actorId) return null;
    const p = actorPos(actorId);
    const { x, y } = f2s(p);
    return (
      <>
        <Circle x={x} y={y} radius={22} stroke="#fbbf24" strokeWidth={3} dash={[6, 4]} opacity={0.9} />
        {a.label && (
          <Text x={x + 24} y={y - 28} text={a.label} fontSize={12} fontStyle="bold" fill="#fbbf24" />
        )}
      </>
    );
  }
  if (a.type === 'spotlight') {
    // 2D parity with AnnotationRenderer3D._spotlight: a strong gold ring on
    // each highlighted actor. The dimming of non-target actors is applied
    // in the actor layer (see actorOpacity below) — we only draw the ring
    // here because Konva renders this layer above the actors.
    const ids = a.actors || [];
    if (!ids.length) return null;
    const colorStr = (() => {
      const raw = a.style?.color;
      if (!raw) return '#ffd700';
      const s = String(raw).replace(/^0x/i, '').replace(/^#/, '');
      return `#${s.padStart(6, '0').slice(-6)}`;
    })();
    return (
      <>
        {ids.map((id, idx) => {
          const p = actorPos(id);
          const { x, y } = f2s(p);
          return (
            <React.Fragment key={`spot-${id}-${idx}`}>
              <Circle x={x} y={y} radius={26} stroke={colorStr} strokeWidth={3} opacity={0.95} />
              <Circle x={x} y={y} radius={20} stroke={colorStr} strokeWidth={1} opacity={0.55} />
            </React.Fragment>
          );
        })}
        {a.label && (
          <Text
            x={f2s(actorPos(ids[0])).x + 28}
            y={f2s(actorPos(ids[0])).y - 30}
            text={a.label}
            fontSize={12}
            fontStyle="bold"
            fill={colorStr}
          />
        )}
      </>
    );
  }
  if (a.type === 'zone') {
    const center = a.position || { x: 50, y: 26.65 };
    const { x, y } = f2s(center);
    const w = (a.style?.width_yards || 18) * sx;
    const h = (a.style?.height_yards || 16) * sy;
    return (
      <>
        <Rect
          x={x - w / 2}
          y={y - h / 2}
          width={w}
          height={h}
          fill={a.style?.color || '#fbbf24'}
          opacity={a.style?.alpha ?? 0.18}
          stroke="#fbbf24"
          strokeWidth={1}
          dash={[4, 4]}
        />
        {a.label && (
          <Text x={x - 40} y={y - h / 2 - 14} text={a.label} fontSize={11} fontStyle="bold" fill="#fbbf24" />
        )}
      </>
    );
  }
  // bracket / matchup / label — render as a label only for now
  if (a.label) {
    const actorId = (a.actors || [])[0];
    if (!actorId) return null;
    const p = actorPos(actorId);
    const { x, y } = f2s(p);
    return <Text x={x + 16} y={y + 16} text={a.label} fontSize={10} fill="#e0f2fe" />;
  }
  return null;
}

// ─── Telestration rendering ──────────────────────────────────────────────────
//
// 2D parity with AnnotationRenderer3D._telestration. Coaches see exactly
// the same shapes here that appear during runtime playback in 3D, so the
// chalk-talk authoring surface and the runtime never disagree.
function TelestrationStrokeRenderer({ stroke, f2s, sx, sy, defaultColor }) {
  if (!stroke || !stroke.points || stroke.points.length === 0) return null;
  // Whiteboard mode passes defaultColor='#1a1a1a' so shapes that were
  // drawn as gold (#ffd700, the editor default) read clearly against
  // the new white surface.
  const color = stroke.color && stroke.color !== '#ffd700'
    ? stroke.color
    : (defaultColor || stroke.color || '#ffd700');
  const sw = Math.max(1.5, (stroke.stroke_width || 0.4) * Math.max(sx, sy) / 2);
  const stagePts = stroke.points.map((p) => f2s(p));

  if (stroke.type === 'arrow') {
    if (stagePts.length < 2) return null;
    const [a, b] = [stagePts[0], stagePts[stagePts.length - 1]];
    return (
      <Arrow
        points={[a.x, a.y, b.x, b.y]}
        stroke={color}
        fill={color}
        strokeWidth={sw}
        pointerLength={Math.max(8, sw * 3)}
        pointerWidth={Math.max(7, sw * 2.5)}
        dash={stroke.dashed ? [6, 4] : undefined}
        opacity={0.95}
      />
    );
  }
  if (stroke.type === 'line') {
    if (stagePts.length < 2) return null;
    const [a, b] = [stagePts[0], stagePts[stagePts.length - 1]];
    return (
      <Line
        points={[a.x, a.y, b.x, b.y]}
        stroke={color}
        strokeWidth={sw}
        dash={stroke.dashed ? [6, 4] : undefined}
        opacity={0.95}
        lineCap="round"
      />
    );
  }
  if (stroke.type === 'freehand') {
    if (stagePts.length < 2) return null;
    const flat = stagePts.flatMap((p) => [p.x, p.y]);
    return (
      <Line
        points={flat}
        stroke={color}
        strokeWidth={sw}
        opacity={0.95}
        lineCap="round"
        lineJoin="round"
        tension={0.4}
        dash={stroke.dashed ? [6, 4] : undefined}
      />
    );
  }
  if (stroke.type === 'circle') {
    if (stagePts.length < 2) return null;
    const [c, edge] = [stagePts[0], stagePts[1]];
    const radius = Math.hypot(edge.x - c.x, edge.y - c.y);
    return (
      <Circle
        x={c.x}
        y={c.y}
        radius={Math.max(4, radius)}
        stroke={color}
        strokeWidth={sw}
        opacity={0.95}
        dash={stroke.dashed ? [6, 4] : undefined}
        fill={stroke.fill || undefined}
      />
    );
  }
  if (stroke.type === 'rect') {
    if (stagePts.length < 2) return null;
    const [a, b] = [stagePts[0], stagePts[1]];
    return (
      <Rect
        x={Math.min(a.x, b.x)}
        y={Math.min(a.y, b.y)}
        width={Math.abs(b.x - a.x)}
        height={Math.abs(b.y - a.y)}
        stroke={color}
        strokeWidth={sw}
        dash={stroke.dashed ? [6, 4] : undefined}
        opacity={0.95}
        fill={stroke.fill || undefined}
      />
    );
  }
  if (stroke.type === 'text') {
    const p = stagePts[0];
    if (!p || !stroke.text) return null;
    return (
      <Text
        x={p.x}
        y={p.y}
        text={stroke.text}
        fontSize={14}
        fontStyle="bold"
        fill={color}
      />
    );
  }
  return null;
}

// Multi-segment polyline + arrow on the final segment.
function RouteWaypoints({ startField, waypoints, f2s, label, draft = false, color }) {
  const points = [startField, ...waypoints];
  const stagePts = points.map((p) => f2s(p));

  // Flat array: [x1,y1, x2,y2, ...]
  const flat = stagePts.flatMap((p) => [p.x, p.y]);
  const lastSegStart = stagePts[stagePts.length - 2] || stagePts[0];
  const lastSegEnd = stagePts[stagePts.length - 1];

  // color prop overrides; draft=yellow; default=white
  const stroke = color || (draft ? '#fbbf24' : '#ffffff');
  const strokeWidth = draft ? 2 : 2.5;

  return (
    <>
      {/* Polyline for all but the last segment */}
      {stagePts.length >= 2 && (
        <Line
          points={flat.slice(0, (stagePts.length - 1) * 2)}
          stroke={stroke}
          strokeWidth={strokeWidth}
          dash={draft ? [6, 4] : undefined}
          lineJoin="round"
          opacity={0.95}
        />
      )}
      {/* Final segment as an arrow */}
      <Arrow
        points={[lastSegStart.x, lastSegStart.y, lastSegEnd.x, lastSegEnd.y]}
        stroke={stroke}
        fill={stroke}
        strokeWidth={strokeWidth}
        pointerLength={10}
        pointerWidth={9}
      />
      {/* Waypoint dots */}
      {stagePts.slice(1, -1).map((p, i) => (
        <Circle key={`wp-${i}`} x={p.x} y={p.y} radius={3} fill={stroke} opacity={0.85} />
      ))}
      {label && (
        <Text x={lastSegEnd.x + 6} y={lastSegEnd.y - 14} text={label} fontSize={11} fontStyle="bold" fill={stroke} />
      )}
    </>
  );
}

